import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import type { PageDto } from '@/lib/pagination';

export interface PatientMetadata {
  custom_fields?: Record<string, string>;
  medical_history?: string;
  surgical_history?: string;
  family_history?: string;
  social_history?: string;
  obstetric_history?: string;
  medications?: string;
  referral_history?: {
    field: string;
    value: string;
    quote?: string;
    page?: number;
    document_id: string;
    revision_id?: string;
    conflict?: boolean;
    imported_at?: string;
  }[];
  intake_status?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  email?: string;
  address?: string;
  allergies?: string[];
  insurance?: {
    provider: string;
    policy_number: string;
    group_number?: string;
  };
  emergency_contact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface PatientOut {
  id: string;
  organization_id: string;
  medical_record_number: string;
  patient_source: string | null;
  metadata_: PatientMetadata | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type PaginatedPatients = PageDto<PatientOut>;

export interface PatientCreateData {
  medical_record_number?: string;
  patient_source?: string;
  metadata_?: PatientMetadata;
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/patients`;
}

export function usePatients(params?: { limit?: number; offset?: number; search?: string; allPages?: boolean }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['patients', orgId, params],
    queryFn: async () => {
      const { allPages, ...query } = params ?? {};
      const { data } = await clientApi.get<PaginatedPatients>(orgUrl(orgId!), { params: query });
      if (allPages) {
        for (let page = 2; page <= data.meta.total_pages; page++) {
          const next = await clientApi.get<PaginatedPatients>(orgUrl(orgId!), { params: { ...query, page } });
          data.data.push(...next.data.data);
        }
      }
      return data;
    },
    enabled: !!orgId,
  });
}

export function usePatient(patientId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['patients', orgId, patientId],
    queryFn: async () => {
      const { data } = await clientApi.get<PatientOut>(`${orgUrl(orgId!)}/${patientId}`);
      return data;
    },
    enabled: !!orgId && !!patientId,
  });
}

export function useCreatePatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PatientCreateData) => {
      const { data } = await clientApi.post<PatientOut>(orgUrl(orgId!), body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}

export function useDeletePatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${patientId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}

export function useBulkDeletePatients() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientIds: string[]) => {
      const results = await Promise.allSettled(patientIds.map((id) => clientApi.delete(`${orgUrl(orgId!)}/${id}`)));
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} of ${patientIds.length} patients`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}

export function useUpdatePatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ patientId, metadata_ }: { patientId: string; metadata_: PatientMetadata }) => {
      const { data } = await clientApi.patch<PatientOut>(`${orgUrl(orgId!)}/${patientId}`, { metadata_ });
      return data;
    },
    onSuccess: (updated) => {
      // Seed the detail cache from the response, then refresh list + detail.
      queryClient.setQueryData(['patients', orgId, updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}
